// Small wrapper around anime.js v4 for the animations the dashboard uses.
// Keeping these in one place means tweaking the easing / durations app-wide
// is a one-line change, and it shields call-sites from the v4 API.
import { animate, createTimeline, utils } from "animejs";

// ---- Shared timing ---------------------------------------------------------

export const EASE = "outQuint";
export const FAST = 280;
export const MEDIUM = 480;
export const SLOW = 720;

// ---- Animations ------------------------------------------------------------

/**
 * Fades + slides an element up into place. Good for page contents on mount.
 */
export const fadeUp = (target, { delay = 0, duration = MEDIUM } = {}) => {
  utils.set(target, { opacity: 0, translateY: 16 });
  return animate(target, {
    opacity: [0, 1],
    translateY: [16, 0],
    duration,
    delay,
    ease: EASE,
  });
};

/**
 * Stagger-fades a list of nodes (e.g. table rows, sidebar items).
 */
export const staggerIn = (targets, { stagger = 40, delay = 0, duration = FAST } = {}) => {
  utils.set(targets, { opacity: 0, translateY: 10 });
  return animate(targets, {
    opacity: [0, 1],
    translateY: [10, 0],
    duration,
    delay: (_el, i) => delay + i * stagger,
    ease: EASE,
  });
};

/**
 * Counts a numeric DOM node from 0 to `value`, formatting with `format`.
 * Used by the dashboard's stat widgets.
 */
export const countTo = (target, value, { duration = SLOW, format = (n) => Math.round(n).toLocaleString() } = {}) => {
  const state = { n: 0 };
  return animate(state, {
    n: [0, value],
    duration,
    ease: EASE,
    onUpdate: () => {
      if (target) target.textContent = format(state.n);
    },
  });
};

/**
 * Tiny scale pulse — handy as an attention nudge on a button or icon.
 */
export const pulse = (target) =>
  animate(target, {
    scale: [{ to: 1.04, duration: 140 }, { to: 1, duration: 200 }],
    ease: EASE,
  });

export { createTimeline, utils };
